import {
  CompanyName,
  CompanyPhoneNumber,
  FACEBOOK_BUSINESS_URL,
  FULL_BUSINESS_URL,
  INSTAGRAM_BUSINESS_URL,
} from '../Constants';

// Home page
export const homePageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Cat App',
  url: FULL_BUSINESS_URL,
  description:
    'Cat App is the joyful home for cat lovers — endless happy cat videos, your own Catigotchi companion, and shareable cat profiles.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${FULL_BUSINESS_URL}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
  sameAs: [FACEBOOK_BUSINESS_URL, INSTAGRAM_BUSINESS_URL],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: CompanyPhoneNumber,
    contactType: 'Customer Service',
    areaServed: 'GB',
    availableLanguage: ['English'],
  },
};

export const homePageAdditionalMeta = [
  { property: 'og:title', content: 'Cat App — Happy Cats Only' },
  {
    property: 'og:description',
    content:
      'Stream adorable cat videos, play with your Catigotchi, and share your own cats with the world.',
  },
  { property: 'og:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
  { property: 'og:url', content: FULL_BUSINESS_URL },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: 'Cat App — Happy Cats Only' },
  {
    name: 'twitter:description',
    content:
      'Endless cat videos, Catigotchi pets, and shareable cat profiles — all in one app.',
  },
  { name: 'twitter:image', content: `${FULL_BUSINESS_URL}/brand/logo.png` },
];

// Forgotten password
export const forgottenPasswordPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Reset Password',
  description:
    'Reset your Cat App password and get back to your happy cat videos, Catigotchi, and My Cats profiles.',
  url: `${FULL_BUSINESS_URL}/forgotten-password`,
};

export const forgottenPasswordPageAdditionalMeta = [
  { property: 'og:title', content: 'Reset Password - Cat App' },
  {
    property: 'og:description',
    content:
      'Securely reset your Cat App password and continue caring for your Catigotchi and watching happy cat videos.',
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/reset-password-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/forgotten-password` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: 'Reset Password - Cat App' },
  {
    name: 'twitter:description',
    content:
      'Follow the simple steps to reset your Cat App account and rejoin the fun.',
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/reset-password-preview.jpg`,
  },
];

// Login
export const loginPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Login',
  description:
    'Log in to Cat App and access your Catigotchi, cat videos, and My Cats profiles.',
  url: `${FULL_BUSINESS_URL}/login`,
};

export const loginPageAdditionalMeta = [
  { property: 'og:title', content: 'Login - Cat App' },
  {
    property: 'og:description',
    content:
      'Access your Cat App account to play with your Catigotchi, enjoy cat videos, and share your pets.',
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/login-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/login` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: 'Login - Cat App' },
  {
    name: 'twitter:description',
    content: 'Sign in to unlock your cats, Catigotchi, and happy video feed.',
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/login-preview.jpg`,
  },
];

// Register
export const registerPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Register',
  description: `Join ${CompanyName} and create your account for unlimited cat joy, Catigotchi companions, and shareable pet profiles.`,
  url: `${FULL_BUSINESS_URL}/register`,
};

export const registerPageAdditionalMeta = [
  { property: 'og:title', content: 'Register - Cat App' },
  {
    property: 'og:description',
    content:
      'Sign up for Cat App to unlock Catigotchi, cat videos, leaderboards, and My Cats profiles.',
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/register-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/register` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: 'Register - Cat App' },
  {
    name: 'twitter:description',
    content:
      'Create your Cat App account today and start enjoying endless happy cats.',
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/register-preview.jpg`,
  },
];

// Maintenance
export const maintenancePageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Maintenance',
  description: `${CompanyName} is undergoing maintenance. We’ll be back soon with more happy cats and Catigotchi fun.`,
  url: `${FULL_BUSINESS_URL}/maintenance`,
};

export const maintenancePageAdditionalMeta = [
  { property: 'og:title', content: `We'll Be Back Soon - ${CompanyName}` },
  {
    property: 'og:description',
    content: `Cat App is currently under maintenance. We’ll return shortly with happy cats and new features.`,
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/maintenance-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/maintenance` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `We'll Be Back Soon - ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Hang tight — Cat App is getting even better. More happy cats coming soon.`,
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/maintenance-preview.jpg`,
  },
];

// Terms & Policies
export const termsAndPoliciesStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms and Policies',
  description: `Review the terms of service and privacy policies of ${CompanyName}.`,
  url: `${FULL_BUSINESS_URL}/terms-and-policies`,
  mainEntity: {
    '@type': 'FAQPage',
    name: 'Terms and Policies FAQ',
    description: `Frequently asked questions about Cat App’s terms, policies, and guidelines.`,
  },
};

export const termsAndPoliciesAdditionalMeta = [
  { property: 'og:title', content: `Terms and Policies - ${CompanyName}` },
  {
    property: 'og:description',
    content: `Learn about the terms, privacy policies, and community rules of ${CompanyName}.`,
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/terms-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/terms-and-policies` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Terms and Policies - ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Understand the policies and community guidelines that keep Cat App safe and fun.`,
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/terms-preview.jpg`,
  },
];

// Error Page
export const errorPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '404 Error Page',
  description: `The requested page could not be found on ${CompanyName}.`,
  url: `${FULL_BUSINESS_URL}/404`,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${FULL_BUSINESS_URL}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export const errorPageAdditionalMeta = [
  { property: 'og:title', content: '404 Page Not Found - Cat App' },
  {
    property: 'og:description',
    content: `Oops! That page doesn’t exist. Head back to ${CompanyName} for more happy cats.`,
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/404-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/404` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: '404 Page Not Found - Cat App' },
  {
    name: 'twitter:description',
    content: `This page isn’t here. Explore endless cat videos and Catigotchi fun on ${CompanyName}.`,
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/404-preview.jpg`,
  },
];

// Contact
export const contactPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  mainEntity: {
    '@type': 'Organization',
    name: CompanyName,
    url: FULL_BUSINESS_URL,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: CompanyPhoneNumber,
      contactType: 'Customer Service',
      areaServed: 'GB',
      availableLanguage: ['English'],
    },
    sameAs: [FACEBOOK_BUSINESS_URL, INSTAGRAM_BUSINESS_URL],
    logo: {
      '@type': 'ImageObject',
      url: `${FULL_BUSINESS_URL}/brand/logo.png`,
    },
  },
};

export const contactPageAdditionalMeta = [
  { property: 'og:title', content: `Contact ${CompanyName}` },
  {
    property: 'og:description',
    content: `Need help with Cat App? Contact ${CompanyName} for support, feedback, or partnership opportunities.`,
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/brand/contact-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/contact` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: `Contact ${CompanyName}` },
  {
    name: 'twitter:description',
    content: `Get in touch with ${CompanyName} for support, feedback, or sponsor opportunities.`,
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/brand/contact-preview.jpg`,
  },
];

// Verify Email page
export const verifyEmailPageStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Verify Email',
  description: `Verify your email address to activate your ${CompanyName} account and unlock all Cat App features.`,
  url: `${FULL_BUSINESS_URL}/verify-email`,
};

export const verifyEmailPageAdditionalMeta = [
  { property: 'og:title', content: 'Verify Your Email - Cat App' },
  {
    property: 'og:description',
    content: `Confirm your email to complete your ${CompanyName} registration and start enjoying Catigotchi and happy cat videos.`,
  },
  {
    property: 'og:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/verify-email-preview.jpg`,
  },
  { property: 'og:url', content: `${FULL_BUSINESS_URL}/verify-email` },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: 'Verify Your Email - Cat App' },
  {
    name: 'twitter:description',
    content: `Secure your ${CompanyName} account by verifying your email address today.`,
  },
  {
    name: 'twitter:image',
    content: `${FULL_BUSINESS_URL}/assets/images/pages/verify-email-preview.jpg`,
  },
];